import {createSlice, type PayloadAction} from "@reduxjs/toolkit";

import type {
    SearchSnapshotItem,
    SearchSnapshotsResult,
    SnapshotStatisticsInterval,
    SnapshotStatisticsPeriod,
    SnapshotStatisticsTotals,
} from "../api/payload-types/snapshot-search";
import {buildApiQueryError, type QueryError} from "../util/errorUtil";
import type {ApiQueryError} from "./types";

/** Page size of the "my snapshots" table (#19113). */
export const SNAPSHOTS_PAGE_SIZE = 20;

/** Selectable statistics bucket sizes, in display order. */
export const SNAPSHOT_STATISTICS_INTERVALS: SnapshotStatisticsInterval[] = ["day", "week", "month", "year"];

/** How many buckets to request per interval (e.g. 30 days, 26 weeks ≙ ~half a year, 24 months ≙ 2 years). */
export const SNAPSHOT_STATISTICS_PERIODS: Record<SnapshotStatisticsInterval, number> = {
    day: 30,
    week: 26,
    month: 24,
    year: 6,
};

/**
 * Which snapshots a tab is showing (#19113): "own" = only the requesting user's (own Historie/DONE
 * tab), "all" = every user's (Admin tab, reva-ownership-admin). Each scope owns an independent copy
 * of the state (list and statistics), so switching between the own and Admin tabs never clobbers
 * the other scope's data (mirrors the per-tab state of the valuations table).
 */
export type SnapshotScope = "own" | "all";

/** Alle Scopes — zum Aufbau des Initial-States und zum Iterieren über beide Scopes. */
export const SNAPSHOT_SCOPES: SnapshotScope[] = ["own", "all"];

/** Statistics of one interval (bucket size), as cached in the slice. */
export interface IntervalStatistics {
    periods: SnapshotStatisticsPeriod[];
    /** Buckets from the user's first snapshot to today (the Ø divisor); 0 if unknown. */
    periodsSinceFirst: number;
}

/**
 * Snapshot search/statistics state of ONE scope. Each scope (own/all) owns a full copy, so
 * switching between the own DONE tab and the Admin tabs never re-fetches or briefly paints the
 * other scope's data — page, rows and cached statistics survive a scope round-trip.
 */
interface SnapshotTabState {
    initialized: boolean;
    loading: boolean;
    pageIndex: number;
    count: number;
    pages: number;
    snapshots: SearchSnapshotItem[];
    queryError: ApiQueryError | null;

    /** Currently selected statistics bucket size. */
    statisticsInterval: SnapshotStatisticsInterval;
    /** Loaded statistics per interval — cached so switching intervals back and forth is instant. */
    statistics: Partial<Record<SnapshotStatisticsInterval, IntervalStatistics>>;
    /** All-time totals (first snapshot day, counts per type) — interval-independent, set on every load. */
    statisticsTotals: SnapshotStatisticsTotals | null;
    statisticsLoading: boolean;
    statisticsError: ApiQueryError | null;
}

/** One independent snapshot search/statistics state per scope. */
type SearchSnapshotsState = Record<SnapshotScope, SnapshotTabState>;

const initialTabState = (): SnapshotTabState => ({
    initialized: false,
    loading: false,
    pageIndex: 0,
    count: 0,
    pages: 0,
    snapshots: [],
    queryError: null,

    statisticsInterval: "month",
    statistics: {},
    statisticsTotals: null,
    statisticsLoading: false,
    statisticsError: null,
});

const initialState = Object.fromEntries(
    SNAPSHOT_SCOPES.map((scope) => [scope, initialTabState()]),
) as SearchSnapshotsState;

export const searchSnapshotsSlice = createSlice({
    name: "searchSnapshots",
    initialState,
    reducers: {
        // Kein Reducer mutiert je in initialState hinein (Immer arbeitet auf Drafts), daher genügt
        // das geteilte initialState-Objekt als Reset-Wert. Setzt bewusst ALLE Scopes zurück
        // (Logout/Lizenzwechsel/resetAll).
        resetSnapshots: () => initialState,
        setPageLoading: (state, action: PayloadAction<{scope: SnapshotScope; pageIndex: number}>) => {
            const tabState = state[action.payload.scope];
            tabState.initialized = true;
            tabState.loading = true;
            tabState.pageIndex = action.payload.pageIndex;
            tabState.queryError = null;
        },
        setPageLoaded: (state, action: PayloadAction<{scope: SnapshotScope; result: SearchSnapshotsResult}>) => {
            const tabState = state[action.payload.scope];
            const {count, pages, snapshots} = action.payload.result;
            tabState.loading = false;
            tabState.count = count;
            tabState.pages = pages;
            tabState.snapshots = snapshots;
        },
        setQueryError: (state, action: PayloadAction<{scope: SnapshotScope; error: QueryError}>) => {
            const tabState = state[action.payload.scope];
            tabState.loading = false;
            tabState.count = 0;
            tabState.pages = 0;
            tabState.snapshots = [];
            tabState.queryError = buildApiQueryError(action.payload.error);
        },
        setStatisticsInterval: (
            state,
            action: PayloadAction<{scope: SnapshotScope; interval: SnapshotStatisticsInterval}>,
        ) => {
            state[action.payload.scope].statisticsInterval = action.payload.interval;
        },
        setStatisticsLoading: (state, action: PayloadAction<SnapshotScope>) => {
            const tabState = state[action.payload];
            tabState.statisticsLoading = true;
            tabState.statisticsError = null;
        },
        setStatisticsLoaded: (
            state,
            action: PayloadAction<{
                scope: SnapshotScope;
                interval: SnapshotStatisticsInterval;
                statistics: IntervalStatistics;
                totals: SnapshotStatisticsTotals;
            }>,
        ) => {
            const tabState = state[action.payload.scope];
            tabState.statisticsLoading = false;
            tabState.statistics[action.payload.interval] = action.payload.statistics;
            tabState.statisticsTotals = action.payload.totals;
        },
        setStatisticsError: (state, action: PayloadAction<{scope: SnapshotScope; error: QueryError}>) => {
            const tabState = state[action.payload.scope];
            tabState.statisticsLoading = false;
            tabState.statisticsError = buildApiQueryError(action.payload.error);
        },
        clearStatisticsCache: (state, action: PayloadAction<SnapshotScope>) => {
            state[action.payload].statistics = {};
        },
    },
    selectors: {
        selectSnapshotsInitialized: (state, scope: SnapshotScope) => state[scope].initialized,
        selectSnapshotsLoading: (state, scope: SnapshotScope) => state[scope].loading,
        selectSnapshotsPageIndex: (state, scope: SnapshotScope) => state[scope].pageIndex,
        selectSnapshotsCount: (state, scope: SnapshotScope) => state[scope].count,
        selectSnapshots: (state, scope: SnapshotScope) => state[scope].snapshots,
        selectSnapshotsQueryError: (state, scope: SnapshotScope) => state[scope].queryError,
        selectStatisticsInterval: (state, scope: SnapshotScope) => state[scope].statisticsInterval,
        selectStatisticsLoading: (state, scope: SnapshotScope) => state[scope].statisticsLoading,
        selectStatisticsError: (state, scope: SnapshotScope) => state[scope].statisticsError,
        selectCurrentStatistics: (state, scope: SnapshotScope) =>
            state[scope].statistics[state[scope].statisticsInterval] ?? null,
        selectStatisticsTotals: (state, scope: SnapshotScope) => state[scope].statisticsTotals,
        selectStatisticsCache: (state, scope: SnapshotScope) => state[scope].statistics,
    },
});

export const {
    resetSnapshots,
    setPageLoading,
    setPageLoaded,
    setQueryError,
    setStatisticsInterval,
    setStatisticsLoading,
    setStatisticsLoaded,
    setStatisticsError,
    clearStatisticsCache,
} = searchSnapshotsSlice.actions;

export const {
    selectSnapshotsInitialized,
    selectSnapshotsLoading,
    selectSnapshotsPageIndex,
    selectSnapshotsCount,
    selectSnapshots,
    selectSnapshotsQueryError,
    selectStatisticsInterval,
    selectStatisticsLoading,
    selectStatisticsError,
    selectCurrentStatistics,
    selectStatisticsTotals,
    selectStatisticsCache,
} = searchSnapshotsSlice.selectors;

export default searchSnapshotsSlice.reducer;
